<template>
  <!-- ▒▒▒ TOP BAR ▒▒▒ -->
  <v-app-bar app flat color="#fafafa" class="text-black" height="60">
    <!-- ☰ shown on xs / sm / md -->
    <v-app-bar-nav-icon class="d-md-none" @click.stop="drawer = !drawer" icon="">
      <template #default>
        <SvgIcon :path="mdiMenu" size="24" />
      </template>
    </v-app-bar-nav-icon>

    <v-toolbar-title>
      <router-link to="/" class="site-title">
        {{ user && (user.active_bar || user.bar)
        ? (user.active_bar || user.bar)
        : 'Star Group' }}
      </router-link>
    </v-toolbar-title>

    <v-spacer />

    <!-- INLINE MENU – only on lg / xl -->
    <div class="d-none d-md-flex">
      <template v-if="!user">
        <v-btn text color="black" to="/login">Login</v-btn>
      </template>

      <template v-else>
        <!-- VIP -->
        <template v-if="user.is_vip">
          <v-btn text color="black" to="/customer/profile">My VIP Profile</v-btn>
          <v-btn text color="black" @click="handleLogout">Logout</v-btn>
        </template>

        <!-- Onboarding -->
        <template v-else-if="user.role === 'onboarding'">
          <v-btn text color="black" to="/">Home</v-btn>
          <v-btn text color="black" to="/handbook">Handbook</v-btn>
          <v-btn text color="black" to="/profile">Profile</v-btn>
          <v-btn text color="black" @click="handleLogout">Logout</v-btn>
        </template>

        <!-- Employee / Manager / Supermanager -->
        <template v-else>
          <v-btn text color="black" to="/">Home</v-btn>
          <v-btn text color="black" to="/daily-tasks">Daily Tasks</v-btn>
          <v-btn text color="black" to="/stock">Stock</v-btn>
          <v-btn text color="black" to="/handbook">Handbook</v-btn>
          <v-btn text color="black" to="/profile">Profile</v-btn>

          <v-btn v-if="['manager','supermanager'].includes(user.role)" text color="black" to="/bi-dashboard">BI</v-btn>
          <v-btn v-if="['manager','supermanager'].includes(user.role)" text color="black" to="/sms-blast">SMS
            Blast</v-btn>

          <!-- Switch Bar -->
          <v-menu v-if="['supermanager','manager'].includes(user.role)" v-model="menu" offset-y>
            <template #activator="{ props }">
              <v-btn text color="black" v-bind="props">
                Switch Bar
                <SvgIcon :path="mdiChevronDown" size="18" style="margin-left:0px;" />
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-for="barName in bars" :key="barName" @click="switchBar(barName)">
                <v-list-item-title>{{ barName }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn text color="black" @click="handleLogout">Logout</v-btn>
        </template>
      </template>
    </div>

    <!-- NOTIFICATION BELL (only for staff) -->
    <v-menu v-if="user && !user.is_vip" v-model="notifMenu" offset-y>
      <template #activator="{ props }">
        <v-btn icon v-bind="props">
          <SvgIcon :path="mdiBell" size="24" />
          <v-badge v-if="unreadNotifications" :content="unreadNotifications" color="red" overlap></v-badge>
        </v-btn>
      </template>

      <!-- Notification dropdown (unchanged) -->
      <v-card style="min-width:300px;max-height:400px;overflow-y:auto;">
        <v-card-title class="d-flex justify-space-between align-center" style="padding:4px 8px;">
          <span style="flex-grow:1;font-size:14px;">Notifications</span>
          <v-btn dense small text color="primary" style="font-size:10px;padding:2px 4px;min-width:auto;"
            @click="markAllRead">Mark all as read</v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-list>
          <v-list-item v-for="n in notifications" :key="n.id" :class="{'unread-notif': !n.is_read}"
            style="cursor:pointer;font-size:14px;padding:4px 8px;" @click="notificationClicked(n)">
            <div style="width:100%;">
              <div class="d-flex align-center justify-space-between">
                <v-list-item-title :class="{'font-weight-bold': !n.is_read}"
                  style="font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-right:8px;">
                  {{ n.message }}
                </v-list-item-title>
                <v-btn icon small class="envelope-btn" @click.stop="toggleNotificationRead(n)">
                  <SvgIcon v-if="n.is_read" :path="mdiEmailOpen" size="20" />
                  <SvgIcon v-else :path="mdiEmail" size="20" />
                </v-btn>
              </div>
              <v-list-item-subtitle style="font-size:12px;margin-top:-4px;">
                {{ formatDate(n.created_at) }}
              </v-list-item-subtitle>
            </div>
          </v-list-item>

          <v-list-item v-if="!notifications.length">
            <v-list-item-title>No notifications</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
  </v-app-bar>

  <!-- ▒▒▒ MOBILE DRAWER ▒▒▒ -->
  <v-navigation-drawer v-model="drawer" temporary location="left" class="d-md-none">
    <v-list dense>
      <!-- GUEST -->
      <template v-if="!user">
        <v-list-item link to="/login" @click="drawer=false">
          <v-list-item-title>Login</v-list-item-title>
        </v-list-item>
      </template>

      <!-- VIP -->
      <template v-else-if="user.is_vip">
        <v-list-item link to="/customer/profile" @click="drawer=false">
          <v-list-item-title>My VIP Profile</v-list-item-title>
        </v-list-item>
        <v-list-item @click="handleLogout">
          <v-list-item-title>Logout</v-list-item-title>
        </v-list-item>
      </template>

      <!-- Onboarding -->
      <template v-else-if="user.role === 'onboarding'">
        <v-list-item link to="/" @click="drawer=false">
          <v-list-item-title>Home</v-list-item-title>
        </v-list-item>
        <v-list-item link to="/handbook" @click="drawer=false">
          <v-list-item-title>Handbook</v-list-item-title>
        </v-list-item>
        <v-list-item link to="/profile" @click="drawer=false">
          <v-list-item-title>Profile</v-list-item-title>
        </v-list-item>
        <v-list-item @click="handleLogout">
          <v-list-item-title>Logout</v-list-item-title>
        </v-list-item>
      </template>

      <!-- Employee / Manager / Supermanager -->
      <template v-else>
        <v-list-item link to="/" @click="drawer=false"><v-list-item-title>Home</v-list-item-title></v-list-item>
        <v-list-item link to="/daily-tasks" @click="drawer=false"><v-list-item-title>Daily Tasks</v-list-item-title></v-list-item>
        <v-list-item link to="/stock" @click="drawer=false"><v-list-item-title>Stock</v-list-item-title></v-list-item>
        <v-list-item link to="/handbook"
          @click="drawer=false"><v-list-item-title>Handbook</v-list-item-title></v-list-item>
        <v-list-item link to="/profile"
          @click="drawer=false"><v-list-item-title>Profile</v-list-item-title></v-list-item>

        <v-list-item v-if="['manager','supermanager'].includes(user.role)" link to="/bi-dashboard"
          @click="drawer=false"><v-list-item-title>BI</v-list-item-title></v-list-item>

        <v-list-item v-if="['manager','supermanager'].includes(user.role)" link to="/sms-blast"
          @click="drawer=false"><v-list-item-title>SMS Blast</v-list-item-title></v-list-item>

        <!-- Collapsible Switch Bar -->
        <v-list-group v-if="['supermanager','manager'].includes(user.role)" no-action>
          <template #activator="{ props }">
            <v-list-item v-bind="props">
              <v-list-item-title class="d-flex align-center">
                <SvgIcon :path="mdiStore" size="20" class="mr-2" />
                <span>Switch Bar</span>
                <v-spacer />
              </v-list-item-title>
            </v-list-item>
          </template>

          <v-list-item v-for="barName in bars" :key="barName" @click="switchBar(barName); drawer=false">
            <v-list-item-title>{{ barName }}</v-list-item-title>
          </v-list-item>
        </v-list-group>

        <v-list-item @click="handleLogout"><v-list-item-title>Logout</v-list-item-title></v-list-item>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import axios from "axios";
import {mdiChevronDown, mdiBell, mdiEmailOpen, mdiEmail, mdiStore, mdiMenu } from "@mdi/js"

export default {
  name: "NavigationBar",
  props: { user: { type: Object, default: null } },
   emits: ['logout', 'barSwitched'],
  data() {
    return {
      drawer: false,
      notifications: [],
      unreadNotifications: 0,
      notifInterval: null,
      bars: [],
      menu: false,
      notifMenu: false,
      mdiChevronDown,
      mdiBell,
      mdiEmailOpen,
      mdiEmail,
      mdiStore,
      mdiMenu,
    };
  },
  beforeUnmount() {
    if (this.notifInterval) {
      clearInterval(this.notifInterval);
      this.notifInterval = null;
    }
  },
  watch: {
    user: {
      handler(u) {
        // Only fetch for non-VIP logged-in staff
        if (!u || u.is_vip) {
          if (this.notifInterval) {
            clearInterval(this.notifInterval);
            this.notifInterval = null;
          }
          this.notifications = [];
          this.unreadNotifications = 0;
        } else {
          // initial fetch + start polling
          this.fetchNotifications();
          if (!this.notifInterval) {
            this.notifInterval = setInterval(() => {
              this.fetchNotifications();
            }, 10000);
          }
        }
        // Bars menu still only for supermanager and manager
        if (u && ["supermanager", "manager"].includes(u.role)) {
          this.fetchBars();
        }
      },
      immediate: true
    }
  },
  methods: {
    /* === original helper methods === */
    async fetchNotifications() {
      try {
        const res = await fetch("/api/notifications/", { credentials:"include" });
        const txt = await res.text();
        const data = txt ? JSON.parse(txt) : { notifications: [] };
        this.notifications = (data.notifications || []).slice(0, 25);
        this.unreadNotifications = data.unread_count || 0;
      } catch (e) {
        console.error("Error fetching notifications:", e);
        this.notifications = [];
      }
    },
    async fetchBars() {
      try {
        const res = await fetch("/api/bars/", { credentials:"include" });
        this.bars = (await res.json()).bars;
      } catch (e) {
        console.error("Error fetching bar list:", e);
      }
    },
    formatDate(dt) {
      return new Date(dt).toLocaleString();
    },
    notificationClicked(n) {
      if (!n.is_read) this.toggleNotificationRead(n);
      this.notifMenu = false;
      if (n.url) this.$router.push(n.url);
    },
    async toggleNotificationRead(n) {
      try {
        const csrf = this.getCookie("csrftoken");
        const res = await fetch("/api/toggle-notification/", {
          method:"POST",
          credentials:"include",
          headers:{
            "X-CSRFToken": csrf,
            "Content-Type":"application/x-www-form-urlencoded",
          },
          body:`notification_id=${n.id}`,
        });
        const data = await res.json();
        if (data.status === "success") {
          if (data.is_read && !n.is_read) {
            this.unreadNotifications = Math.max(this.unreadNotifications - 1, 0);
          } else if (!data.is_read && n.is_read) {
            this.unreadNotifications++;
          }
          n.is_read = data.is_read;
        }
      } catch (e) { console.error("Error toggling notification:", e); }
    },
    async markAllRead() {
      try {
        const csrf = this.getCookie("csrftoken");
        const res  = await fetch("/api/mark-all-read/", {
          method:"POST",
          credentials:"include",
          headers:{
            "X-CSRFToken": csrf,
            "Content-Type":"application/x-www-form-urlencoded",
          },
        });
        const data = await res.json();
        if (data.status === "success") {
          this.unreadNotifications = 0;
          this.notifications = this.notifications.map(n => ({ ...n, is_read:true }));
        }
      } catch (e) { console.error("Error mark‑all‑read:", e); }
    },
    handleLogout() { this.$emit("logout"); },
    async switchBar(barName) {
      try {
        const res = await axios.post("/api/switch-bar/",
                                     { bar: barName },
                                     { withCredentials:true });
        this.$emit("barSwitched", res.data.active_bar);
        this.menu = false;
        if (this.$route.name === "ForumThreadDetail") this.$router.push("/forum");
      } catch (e) { console.error("Error switching bar:", e); }
    },
    getCookie(name) {
      const cookie = document.cookie
        .split(";")
        .map(c => c.trim())
        .find(c => c.startsWith(name + "="));
      return cookie ? decodeURIComponent(cookie.split("=")[1]) : null;
    },
  },
};
</script>

<style scoped>
.site-title      { text-decoration:none; color:black; font-weight:bold; line-height:1.05 }
.font-weight-bold{ font-weight:bold }
.unread-notif    { background:#e0f7ff !important }
.envelope-btn    { width:32px;height:32px;min-width:32px;border-radius:50%;padding:0;
                   display:flex;align-items:center;justify-content:center;position:relative;top:5px }
.envelope-icon   { font-size:20px }
</style>
